import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  House,
  Users,
  SignOut,
  X,
  CalendarDots,
  ListBullets,
  Bell,
  Stack,
} from "@phosphor-icons/react";
import logoImg from "@/assets/logo.png";
import ThemeToggle from "./ThemeToggle";

export default function Sidebar({ open, onClose, unreadCount = 0, onOpenNotifications }) {
  const { user, logout } = useAuth();
  const location = useLocation();

  const links = [
    { to: "/", icon: House, label: "Dashboard", exact: true },
    { to: "/clients", icon: Users, label: "Clienti" },
    { to: "/calendar", icon: CalendarDots, label: "Calendario" },
    { to: "/templates", icon: Stack, label: "Template" },
    { to: "/exercises", icon: ListBullets, label: "Esercizi" },
  ];

  const isActive = (to, exact) => {
    if (exact) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  return (
    <aside
      className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#0F0F0F] border-r border-[#1A1A1A]
        flex flex-col transition-transform duration-300
        ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-5 border-b border-[#1A1A1A]">
        <div className="flex items-center gap-2.5">
          <img src={logoImg} alt="FitPro" className="w-8 h-8 rounded-lg" />
          <span
            className="text-xl font-black tracking-tighter uppercase text-white"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            FITPRO
          </span>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden text-zinc-400 hover:text-white"
          data-testid="sidebar-close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1" data-testid="sidebar-nav">
        <button
          onClick={() => { onOpenNotifications?.(); onClose(); }}
          className="sidebar-link w-full text-left relative"
          data-testid="nav-notifiche"
        >
          <Bell size={20} weight={unreadCount > 0 ? "fill" : "regular"} />
          <span className="text-sm font-medium">Notifiche</span>
          {unreadCount > 0 && (
            <span className="ml-auto min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center leading-none" data-testid="notif-badge-sidebar">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            onClick={onClose}
            className={`sidebar-link ${isActive(link.to, link.exact) ? "active" : ""}`}
            data-testid={`nav-${link.label.toLowerCase()}`}
          >
            <link.icon size={20} weight={isActive(link.to, link.exact) ? "fill" : "regular"} />
            <span className="text-sm font-medium">{link.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-[#1A1A1A] p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center text-white text-sm font-bold">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name || "Utente"}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-zinc-400 hover:text-red-400 rounded-md hover:bg-red-500/10 transition-colors"
          data-testid="logout-button"
        >
          <SignOut size={18} />
          Esci
        </button>
        <div className="mt-3 pt-3 border-t border-[#1A1A1A] flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Tema</span>
          <ThemeToggle />
        </div>
      </div>
    </aside>
  );
}
